import { Alert, PageHeader, SkeletonRows } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AccountingPage, companySearchShape } from "../../../../features/accounting/company.js";
import { EntryEditor } from "../../../../features/accounting/entry-editor.js";
import { EntryForm } from "../../../../features/accounting/entry-form.js";
import { todayIstanbul, useJournalEntry } from "../../../../features/accounting/shared.js";
import { errorMessage } from "../../../../lib/errors.js";
import { useDocumentTitle } from "../../../../lib/hooks.js";

const searchSchema = z.object({
  ...companySearchShape,
  /** Kopyalanacak fiş: satırları ve başlığı yeni taslağa taşınır. */
  copy: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/muhasebe/fisler/yeni")({
  validateSearch: (search) => searchSchema.parse(search),
  component: NewEntryPage,
});

function useBreadcrumb() {
  const { t } = useTranslation();
  return [
    {
      label: t("menu.journalEntries"),
      link: (children: ReactNode, className: string): ReactElement => (
        <Link to="/muhasebe/fisler" className={className}>
          {children}
        </Link>
      ),
    },
    { label: t("accounting.entries.new") },
  ];
}

function NewEntryPage() {
  const { t } = useTranslation();
  const { copy } = Route.useSearch();
  const breadcrumb = useBreadcrumb();
  const source = useJournalEntry(copy);
  useDocumentTitle(t("accounting.entries.new"));

  if (copy && !source.data)
    return (
      <>
        <PageHeader
          title={t("accounting.entries.new")}
          breadcrumb={[{ label: t("menu.groups.accounting") }, ...breadcrumb]}
        />
        {source.error ? <Alert tone="danger">{errorMessage(source.error)}</Alert> : <SkeletonRows rows={8} />}
      </>
    );

  return (
    <AccountingPage
      title={t("accounting.entries.new")}
      description={t("accounting.entry.description")}
      breadcrumb={breadcrumb}
      {...(copy && source.data ? { fixedCompanyId: source.data.companyId } : {})}
    >
      {({ companyId }) =>
        copy && source.data ? (
          <EntryEditor key={source.data.id} entry={source.data} mode="copy" />
        ) : (
          <EntryForm
            companyId={companyId}
            initialHeader={{
              entryType: "MAHSUP",
              documentDate: todayIstanbul(),
              postingDate: todayIstanbul(),
              description: "",
            }}
            initialLines={[]}
            saved={null}
          />
        )
      }
    </AccountingPage>
  );
}
